<template>
  <div style="height: 100%;">
    <v-skeleton-loader
      v-if="loading"
      type="card"
      height="400"
    ></v-skeleton-loader>
    <v-card v-else class="pt-5 pb-6 px-6" rounded="lg" style="height: 100%;">
      <div class="d-flex flex-column ga-3">
        <div class="d-flex align-center">
          <div class="text-body-1">{{ $t('assetStatusBreakdown') }}</div>
          <v-btn
            class="ml-auto my-n1 opacity-60"
            icon="mdi-dots-horizontal"
            size="32"
            variant="text"
          />
        </div>
        <!-- Custom bar chart using divs -->
        <div class="status-bar-chart" v-if="bars.length > 0">
          <div
            v-for="(bar, i) in bars"
            :key="i"
            class="status-bar"
            :style="{
              backgroundColor: bar.color,
              opacity: barOpacity(bar)
            }"
            @click="toggleStatus(bar)"
          ></div>
        </div>
        <div v-else class="d-flex align-center justify-center" style="height: 40px;">
          <span class="text-body-2 text-medium-emphasis">{{ $t('noData') }}</span>
        </div>
      </div>
      <v-list class="mt-3 legend-items" v-if="items.length > 0">
        <v-list-item
          v-for="(item, i) in items.filter(item => item.value > 0)"
          :key="item.id || i"
          class="px-2"
          density="compact"
          rounded="lg"
          tabindex="0"
          :value="item.id"
          :active="isSelected(item.id)"
          @click="toggleStatus(item)"
          @blur="blurSegments"
          @focus="focusSegment(item.id)"
          @mouseenter="focusSegment(item.id)"
          @mouseleave="blurSegments"
        >
          <template #prepend>
            <v-avatar class="mr-n2" :color="item.color" size="12" />
          </template>
          <template #title>
            <span class="text-body-2">{{ item.title }}</span>
          </template>
          <template #append>
            <v-chip label size="x-small" :text="`${item.value}%`" />
          </template>
        </v-list-item>
      </v-list>
    </v-card>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { useSelectableFilter } from '@/composables/useSelectableFilter'
import { sqlLiteral } from '@/utils/assetFields'

defineProps({
  cardWidth: {
    type: Number,
    default: null
  }
})

const store = useStore()
const { t } = useI18n()
const focusedItemId = ref(null)

// Clicking a segment or a legend row filters every visual to that status.
const chartName = 'assetStatusBar'
const { isSelected, hasSelection, sync, toggle } = useSelectableFilter(chartName)

const toggleStatus = async (item) => {
  if (!item?.id) return
  await toggle(item.id, `assetStatusId = ${sqlLiteral(item.id)}`, `${t('status')}: ${item.title}`)
}

// Hover dims the other segments; an active filter keeps them dim.
const barOpacity = (bar) => {
  if (focusedItemId.value !== null) return focusedItemId.value === bar.id ? 1 : 0.3
  if (hasSelection.value) return isSelected(bar.id) ? 1 : 0.3
  return 1
}

let focusClearTimeout = 0
function focusSegment(itemId) {
  clearTimeout(focusClearTimeout)
  focusedItemId.value = itemId
}
function blurSegments() {
  focusClearTimeout = setTimeout(() => (focusedItemId.value = null), 100)
}

// Reactive data for status breakdown
const statusData = ref([])
const loading = ref(true)

// Fetch status breakdown data
async function fetchStatusBreakdown() {
  try {
    loading.value = true

    sync()

    // UNIQUE assets by their current status. statusId is selected so the
    // segments can cross-filter (and so the hover highlight works at all).
    const query = `
      SELECT
        st.Id as statusId,
        TRIM(st.Title) as statusTitle,
        TRIM(st.Color) as statusColor,
        MIN(st.Sort) as statusSort,
        COUNT(fa.assetKey) as assetCount
      FROM filteredAssets fa
      LEFT JOIN status_templates st ON fa.assetStatusId = st.Id
      WHERE st.Title IS NOT NULL
      GROUP BY st.Id, TRIM(st.Title), TRIM(st.Color)
      ORDER BY MIN(st.Sort)
    `

    const results = await store.getters.getDBConnection.query(query);

    const data = results.toArray().map((row) => {
      const jsonRow = row.toJSON();
      return {
        ...jsonRow,
        sort: Number(jsonRow.statusSort),
        assetCount: Number(jsonRow.assetCount) || 0,
      };
    });

    // Calculate total assets
    const totalAssets = data.reduce((sum, row) => sum + row.assetCount, 0)

    // Transform data into items with percentages
    statusData.value = data.map(row => ({
      id: row.statusId,
      title: row.statusTitle || 'Unknown Status',
      color: row.statusColor || '#9E9E9E',
      count: row.assetCount,
      value: totalAssets > 0 ? Math.round((row.assetCount / totalAssets) * 100) : 0
    }))

  } catch (error) {
    console.error('Error fetching status breakdown:', error)
    statusData.value = []
  } finally {
    loading.value = false
  }
}


// Initial fetch when data is loaded
watch(
  () => store.getters.getDataLoaded,
  async (loaded) => {
    if (loaded && store.getters.isDuckDBInitialized) {
      await fetchStatusBreakdown()
    }
  },
  { immediate: true }
)

// Also watch for filter changes
watch(
  () => store.state.filterContext?.filtersLastUpdated,
  async () => {
    if (store.getters.isDuckDBInitialized && store.getters.getDataLoaded) {
      await fetchStatusBreakdown()
    }
  },
  { deep: true }
)

// Computed items from status data
const items = computed(() => statusData.value)

// Chart configuration - 50 total bars
const totalBars = 50

// Calculate bar distribution - each status gets whole bars, minimum 1 if value > 0
const bars = computed(() => {
  const filteredItems = items.value.filter(item => item.value > 0)
  if (!filteredItems.length) return []

  const totalValue = filteredItems.reduce((sum, item) => sum + item.value, 0)
  if (totalValue === 0) return []

  // Calculate bar counts for each status
  let barCounts = filteredItems.map(item => {
    const proportion = item.value / totalValue
    return Math.max(1, Math.floor(proportion * totalBars))
  })

  // Distribute remaining bars to largest items
  let assigned = barCounts.reduce((sum, n) => sum + n, 0)
  let remaining = totalBars - assigned

  // Sort indices by value descending for fair distribution
  const sortedIndices = filteredItems
    .map((item, idx) => ({ idx, value: item.value }))
    .sort((a, b) => b.value - a.value)
    .map(x => x.idx)

  let distIdx = 0
  while (remaining > 0) {
    barCounts[sortedIndices[distIdx % sortedIndices.length]]++
    remaining--
    distIdx++
  }

  // Generate individual bars with colors
  const result = []
  filteredItems.forEach((item, index) => {
    const count = barCounts[index]
    for (let i = 0; i < count; i++) {
      result.push({
        id: item.id,
        color: item.color
      })
    }
  })

  return result
})
</script>

<style scoped>
  .legend-items {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;

    & > .v-list-item {
      background-color: rgba(var(--v-theme-on-surface), 0.05);
    }
  }

  .status-bar-chart {
    display: flex;
    gap:5px;
    height:70px;
    align-items: stretch;
    width: 100%;
  }

  .status-bar {
    flex: 1;
    border-radius: 8px;
    transition: opacity 0.25s ease-in-out;
    cursor: pointer;
  }
</style>
